/**
 * Router - URL routing for deep linking
 *
 * Enables bookmarkable and shareable URLs for structures.
 * URL format: /Zelle/Organelle/Zellkern (ancestor path)
 * Also handles legal pages: /impressum, /datenschutz
 */

/**
 * Legal page routes
 */
const LEGAL_ROUTES = ['/impressum', '/datenschutz'];

/**
 * Router class - manages URL state and navigation
 */
export class Router {
  /**
   * Create a new Router
   * @param {import('./map/CellMap.js').CellMap} cellMap - The CellMap instance
   */
  constructor(cellMap) {
    this.cellMap = cellMap;
    this.structureLayer = null;
    this.enabled = false;
    this.notifications = null;

    // Bind popstate handler
    this.handlePopState = this.handlePopState.bind(this);
  }

  /**
   * Set the notifications instance for user feedback
   * @param {import('./ui/Notifications.js').Notifications} notifications
   */
  setNotifications(notifications) {
    this.notifications = notifications;
  }

  /**
   * Initialize router after structures are loaded
   */
  init() {
    this.structureLayer = this.cellMap.getStructureLayer();
    if (!this.structureLayer) {
      console.error('Router: Structure layer not available');
      return;
    }

    // Enable routing
    this.enabled = true;

    // Listen for browser navigation
    window.addEventListener('popstate', this.handlePopState);

    // Handle initial URL
    this.navigateFromUrl();
  }

  /**
   * Parse the current URL and navigate to structure if valid
   */
  navigateFromUrl() {
    const path = window.location.pathname;

    // Skip if at root
    if (path === '/' || path === '') {
      return;
    }

    // Check for legal pages
    if (LEGAL_ROUTES.includes(path)) {
      this.showLegalPage(path.slice(1)); // Remove leading /
      return;
    }

    // Parse path segments: /Zelle/Organelle/Zellkern -> ['Zelle', 'Organelle', 'Zellkern']
    const segments = path.split('/').filter(s => s.length > 0).map(decodeURIComponent);

    if (segments.length === 0) {
      return;
    }

    // Find structure by name (use last segment)
    const targetName = segments[segments.length - 1];
    const structure = this.findStructureByName(targetName);

    if (structure) {
      // Select the structure
      const clickHandler = this.cellMap.getClickHandler();
      if (clickHandler) {
        clickHandler.selectById(structure.id);
      }
    } else {
      // Invalid path - show message and redirect to root
      console.warn('Router: Structure not found:', targetName);
      this.showNotFoundMessage(targetName);
      this.updateUrl('/');
    }
  }

  /**
   * Find a structure by name
   * @param {string} name - Structure name to search for
   * @returns {Object|null} Structure object or null
   */
  findStructureByName(name) {
    const structures = this.structureLayer.getAllStructures();
    const normalizedName = name.toLowerCase();

    return structures.find(s =>
      s.name.toLowerCase() === normalizedName ||
      this.slugify(s.name) === normalizedName
    );
  }

  /**
   * Update URL when structure is selected
   * @param {Object} structure - Structure with ancestorTree
   */
  onStructureSelect(structure) {
    if (!this.enabled) {
      return;
    }

    // Build path from ancestor tree
    const path = this.buildPath(structure.ancestorTree);
    this.updateUrl(path);
  }

  /**
   * Clear URL on deselection
   */
  onStructureDeselect() {
    if (!this.enabled) {
      return;
    }
    this.updateUrl('/');
  }

  /**
   * Build URL path from ancestor tree
   * @param {Array} ancestors - [{id, name}, ...]
   * @returns {string} URL path
   */
  buildPath(ancestors) {
    if (!ancestors || ancestors.length === 0) {
      return '/';
    }

    const segments = ancestors.map(a => encodeURIComponent(a.name));
    return `/${segments.join('/')}`;
  }

  /**
   * Update browser URL without reload
   * @param {string} path - URL path
   */
  updateUrl(path) {
    if (window.location.pathname !== path) {
      window.history.pushState({ path }, '', path);
    }
  }

  /**
   * Handle browser back/forward
   */
  handlePopState() {
    this.navigateFromUrl();
  }

  /**
   * Get current path
   * @returns {string}
   */
  getCurrentPath() {
    return window.location.pathname;
  }

  /**
   * Generate shareable URL for a structure
   * @param {Object} structure - Structure with ancestorTree
   * @returns {string} Full URL
   */
  getShareUrl(structure) {
    if (!structure.ancestorTree) {
      return window.location.origin;
    }

    const path = this.buildPath(structure.ancestorTree);
    return window.location.origin + path;
  }

  /**
   * Create URL-safe slug from name
   * @param {string} name - Name to slugify
   * @returns {string} URL-safe slug
   */
  slugify(name) {
    return name
      .toLowerCase()
      .replace(/[äÄ]/g, 'ae')
      .replace(/[öÖ]/g, 'oe')
      .replace(/[üÜ]/g, 'ue')
      .replace(/ß/g, 'ss')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
  }

  /**
   * Show message for not found structure
   * @param {string} name - Name that wasn't found
   */
  showNotFoundMessage(name) {
    console.warn(`Struktur "${name}" nicht gefunden. Zeige Startseite.`);

    // Show notification if available
    if (this.notifications) {
      this.notifications.warning(`Struktur "${name}" nicht gefunden.`, {
        duration: 5000
      });
    }
  }

  /**
   * Show a legal page (impressum, datenschutz)
   * @param {string} page - Page name (without leading /)
   */
  async showLegalPage(page) {
    try {
      // Dynamically import the page module
      const module = await import(`./pages/${page}.js`);

      // Update document title if meta info available
      if (module.meta && module.meta.title) {
        document.title = module.meta.title;
      }

      // Render the page
      module.render();
    } catch (error) {
      console.error(`Failed to load legal page: ${page}`, error);
      // Redirect to home on error
      this.updateUrl('/');
      window.location.reload();
    }
  }

  /**
   * Check if current path is a legal page
   * @returns {boolean}
   */
  isLegalPage() {
    return LEGAL_ROUTES.includes(window.location.pathname);
  }

  /**
   * Check if router is enabled
   * @returns {boolean}
   */
  isEnabled() {
    return this.enabled;
  }

  /**
   * Clean up event listeners
   */
  destroy() {
    window.removeEventListener('popstate', this.handlePopState);
    this.enabled = false;
  }
}
